import axios from 'axios';

/**
 * Khareef Photos API
 * -------------------
 * Wired to the KhareefPhotosPlugin endpoints:
 *   POST /KhareefPhoto/GetAllBy     — list, needs KhareefPhoto.List
 *   GET  /KhareefPhoto/Get          — single row, needs KhareefPhoto.List
 *   POST /KhareefPhoto/UpdateStatus — needs KhareefPhoto.ChangeStatus
 *   POST /KhareefPhoto/Delete       — needs KhareefPhoto.Delete
 *   POST /KhareefPhoto/GetShared    — published rows only, anonymous
 * The request shapes follow the Gallery module (see ApiGallery.js).
 */

// Statuses used across the moderation flow (steps 4-6 of the campaign).
export const STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  PUBLISHED: 'PUBLISHED',
  REJECTED: 'REJECTED',
};

/**
 * Presentation metadata for each status. The `label` values are also the
 * localization keys (see localization/khareef.js). Shared by the manage
 * list, the details page and the summary cards.
 */
export const STATUS_META = {
  [STATUS.PENDING]: { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-800' },
  [STATUS.APPROVED]: { label: 'Approved', className: 'bg-blue-100 text-blue-800' },
  [STATUS.PUBLISHED]: { label: 'Published', className: 'bg-green-100 text-green-800' },
  [STATUS.REJECTED]: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
};

// Mirrors KhareefPhotosPlugin/DAL/Enums/KhareefPhotoStatusEnum.cs
export const STATUS_ID = {
  [STATUS.PENDING]: 1,
  [STATUS.APPROVED]: 2,
  [STATUS.PUBLISHED]: 3,
  [STATUS.REJECTED]: 4,
};

const STATUS_BY_ID = Object.entries(STATUS_ID).reduce(
  (acc, [name, id]) => ({ ...acc, [id]: name }),
  {},
);

// ---- Error helpers ----
// A completed call answers with { isDone, message, data }. Failures arrive as
// HTTP 409 carrying { IsDone, Message, StatusCode } written by
// ErrorHandlerMiddleware, so both casings are read below.
// 1004 is CustomErrorStatusCode.NotFoundEntity.
const NOT_FOUND_STATUS_CODE = 1004;

const errorBody = (error) => error?.response?.data ?? {};

/** Localization key, or ready to show message, for a failed request. */
export const errorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return 'You are not authorized to manage Khareef photos';
  return errorBody(error).Message || errorBody(error).message || error?.message || '';
};

const isNotFound = (error) =>
  (errorBody(error).StatusCode ?? errorBody(error).statusCode) === NOT_FOUND_STATUS_CODE;

// ---- Mapping ----
const toDateOnly = (value) => (value ? String(value).slice(0, 10) : '');

/**
 * The API stores only the file name the CDN answered with, the same as every
 * other module (see AssetsStyleBlock.js and friends), so the CDN base has to be
 * put back in front of it before it can be rendered. Absolute values are left
 * alone in case a row was ever stored with a full URL.
 */
const toImageUrl = (fileName) => {
  if (!fileName) return '';
  if (/^(https?:)?\/\//i.test(fileName) || fileName.startsWith('data:')) return fileName;
  return `${window.env.REACT_APP_IMAGE_PATH}${fileName}`;
};

/** KhareefPhotoVM -> the shape the pages render. */
const mapPhoto = (vm) => ({
  id: vm.id,
  url: toImageUrl(vm.fileUrl),
  thumbnailUrl: toImageUrl(vm.thumbnailUrl),
  participant: vm.fullName,
  mobileNumber: vm.mobileNumber,
  // Stored bare by the API, but a stray "@" from an older row would otherwise
  // produce instagram.com/@name, which 404s.
  instagram: (vm.instagram || '').replace(/^@+/, '') || undefined,
  title: vm.photoTitle,
  location: vm.location,
  description: vm.description,
  referenceNumber: vm.referenceNumber,
  submittedAt: toDateOnly(vm.createDate),
  status: STATUS_BY_ID[vm.statusId] ?? STATUS.PENDING,
  rejectionReason: vm.rejectionReason,
});

/** SharedKhareefPhotoVM -> the same shape, without the moderation fields. */
const mapSharedPhoto = (vm) => ({
  id: vm.id,
  url: toImageUrl(vm.fileUrl),
  thumbnailUrl: toImageUrl(vm.thumbnailUrl),
  participant: vm.fullName,
  title: vm.photoTitle,
  location: vm.location,
  submittedAt: toDateOnly(vm.createDate),
  // The endpoint answers with published rows only and carries no status field.
  status: STATUS.PUBLISHED,
});

// ---- Requests ----

// Get all submissions (optionally filtered by status).
export const getKhareefPhotos = async (e) => {
  const filter = e?.queryKey?.[1] ?? {};
  const query = await axios.post('/KhareefPhoto/GetAllBy', {
    statusId: filter.statusId ?? 0, // 0 returns every status
    // The dashboard cards, filter tabs, search and pagination all work over the
    // whole list, so paging stays off here and stays client side.
    paginationFilter: { isPaging: false, ...(filter.paginationFilter ?? {}) },
  });
  const { isDone, message, data, paginationResult } = query.data;
  if (!isDone) throw new Error(message);
  return { data: (data ?? []).map(mapPhoto), isDone, paginationResult };
};

// Get a single submission.
export const getKhareefPhoto = async (e) => {
  const id = e?.queryKey?.[1];
  try {
    const query = await axios.get(`/KhareefPhoto/Get?id=${id}&langId=0`);
    const { isDone, message, data } = query.data;
    if (!isDone) throw new Error(message);
    return { data: data ? mapPhoto(data) : null, isDone };
  } catch (error) {
    // A removed or unknown id is an empty state, not a failure.
    if (isNotFound(error)) return { data: null, isDone: true };
    throw error;
  }
};

// Update a submission status (Approve / Reject / Publish).
// `reason` carries the rejection reason when status === REJECTED.
export const setKhareefPhotoStatus = async ({ id, status, reason }) => {
  const query = await axios.post('/KhareefPhoto/UpdateStatus', {
    id,
    khareefPhotoStatus: STATUS_ID[status],
    rejectionReason: status === STATUS.REJECTED ? reason : null,
  });
  const { isDone, message } = query.data;
  if (!isDone) throw new Error(message);
  // The endpoint answers with a result envelope only, so the caller is handed
  // back the row it just changed.
  return { data: { id, status, reason }, isDone, message };
};

export const deleteKhareefPhoto = async (id) => {
  const query = await axios.post('/KhareefPhoto/Delete', { id });
  const { isDone, message } = query.data;
  if (!isDone) throw new Error(message);
  return { data: { id }, isDone, message };
};

// Published submissions for the public preview page.
export const getSharedKhareefPhotos = async (e) => {
  const filter = e?.queryKey?.[1] ?? {};
  const query = await axios.post('/KhareefPhoto/GetShared', {
    paginationFilter: { isPaging: false, ...(filter.paginationFilter ?? {}) },
  });
  const { isDone, message, data, paginationResult } = query.data;
  if (!isDone) throw new Error(message);
  return { data: (data ?? []).map(mapSharedPhoto), isDone, paginationResult };
};
